/**
 * MotivationScorecardPanel — dockable strategy-canvas panel that shows the
 * motivation scorecard for a strategy × its audience and lets the operator
 * (re)compute it. Mirrors the CanvasTasksPanel docking pattern (fixed, over the
 * canvas), but anchored bottom-RIGHT so it doesn't collide with the tasks panel.
 *
 * Data flow:
 *  - on open: hydrate the last-computed card from the strategy doc field
 *    `motivationScorecard` (StrategyService.get).
 *  - "Score" / "Re-score": call the synchronous agent endpoint
 *    (AgentApiClient.scoreStrategy) — the SAME engine the CLI `internal strategy
 *    score` drives — and replace the card with the fresh result.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';

interface ActorScore {
  actorId: string;
  actorName?: string;
  weight: number;
  converted: boolean;
  dropIndex: number | null;
  dropLabel: string | null;
  dominantNeed: string;
  initialMotivation: number;
  finalBudget: number;
  minBudget: number;
}

interface Scorecard {
  strategyId?: string;
  audienceId?: string;
  screenCount: number;
  actorCount: number;
  totalWeight: number;
  predictedConversion: number;
  converted: number;
  dropped: number;
  perActor: ActorScore[];
  dropHotspots: Array<{ screenIndex: number; label: string; weight: number; actorIds: string[] }>;
  underservedNeeds: Array<{ needId: string; group: string; demandWeight: number; servedByScreens: number }>;
  violations: Array<{ type: string; screenIndex: number; label: string; message: string; affectedWeight: number }>;
  /** Parallel index→identity map (score order) so violations/hotspots are navigable. */
  screenRefs?: Array<{ blockId?: string; blockEntityId?: string; screenId?: string; label?: string }>;
  computedAt?: string;
  storedProfiles?: number;
  derivedProfiles?: number;
}

export interface ScorecardScreenRef {
  blockId?: string;
  blockEntityId?: string;
  screenId?: string;
  label?: string;
}

interface MotivationScorecardPanelProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  strategyId: string;
  strategyName?: string;
  /** Focus the strategy canvas on the screen/block behind a violation or hotspot. */
  onJumpToScreen?: (ref: ScorecardScreenRef, screenIndex: number) => void;
}

const pct = (x: number): string => `${Math.round((x ?? 0) * 100)}%`;

/** Most frequent dominant need across actors (the cohort's dominant need). */
function cohortDominantNeed(perActor: ActorScore[]): string | null {
  const counts = new Map<string, number>();
  for (const a of perActor) {
    if (!a.dominantNeed) continue;
    counts.set(a.dominantNeed, (counts.get(a.dominantNeed) ?? 0) + (a.weight || 1));
  }
  let best: string | null = null;
  let bestN = -1;
  for (const [need, n] of counts) {
    if (n > bestN) { best = need; bestN = n; }
  }
  return best;
}

export const MotivationScorecardPanel: React.FC<MotivationScorecardPanelProps> = ({
  open,
  onClose,
  projectId,
  strategyId,
  strategyName,
  onJumpToScreen,
}) => {
  const [card, setCard] = useState<Scorecard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the last-computed card from the strategy doc when the panel opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const svc = new StrategyService(projectId);
        const strategy = await svc.get(strategyId);
        const persisted = (strategy as any)?.motivationScorecard as Scorecard | undefined;
        if (!cancelled && persisted) setCard(persisted);
      } catch (e) {
        // non-fatal — the operator can still Score
        console.warn('[MotivationScorecardPanel] failed to hydrate persisted scorecard', e);
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId, strategyId]);

  const runScore = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = new AgentApiClient();
      const res = await api.scoreStrategy({ projectId, strategyId, persist: true });
      if (!res.success || !res.scorecard) {
        throw new Error(res.error || 'Scoring failed');
      }
      setCard(res.scorecard as Scorecard);
    } catch (e: any) {
      setError(e?.message || 'Scoring failed');
    } finally {
      setLoading(false);
    }
  }, [projectId, strategyId]);

  if (!open) return null;

  const dominant = card ? cohortDominantNeed(card.perActor) : null;

  return (
    <Box
      data-testid="motivation-scorecard-panel"
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1250,
        width: 380,
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <IconButton size="small" onClick={onClose} data-testid="motivation-scorecard-close" aria-label="Close scorecard">
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Motivation Scorecard{strategyName ? ` · ${strategyName}` : ''}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
          onClick={runScore}
          disabled={loading}
          data-testid="motivation-scorecard-score-btn"
          sx={{ textTransform: 'none' }}
        >
          {card ? 'Re-score' : 'Score'}
        </Button>
      </Stack>

      {loading && <LinearProgress data-testid="motivation-scorecard-loading" />}

      <Box sx={{ overflowY: 'auto', p: 1.5 }}>
        {error && (
          <Typography variant="body2" color="error" data-testid="motivation-scorecard-error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        {!card && !loading && (
          <Typography variant="body2" color="text.secondary" data-testid="motivation-scorecard-empty">
            No scorecard yet. Press <b>Score</b> to run the motivation engine across this strategy's
            screens and audience.
          </Typography>
        )}

        {card && (
          <Stack spacing={1.5}>
            {/* Predicted conversion */}
            <Box data-testid="motivation-scorecard-conversion">
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2" color="text.secondary">Predicted conversion</Typography>
                <Typography variant="h6" sx={{ lineHeight: 1 }}>{pct(card.predictedConversion)}</Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, (card.predictedConversion ?? 0) * 100))}
                color={card.predictedConversion >= 0.5 ? 'success' : 'warning'}
                sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary">
                {card.converted}/{card.actorCount} convert · {card.dropped} dropped · {card.screenCount} screens
              </Typography>
            </Box>

            {dominant && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Dominant cohort need</Typography>
                <Chip size="small" label={dominant} />
              </Stack>
            )}

            <Divider />

            {/* Per actor */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Per actor</Typography>
              <Stack spacing={0.5}>
                {card.perActor.map((a) => (
                  <Stack
                    key={a.actorId}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    data-testid={`motivation-scorecard-actor-${a.actorId}`}
                  >
                    {a.converted
                      ? <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                      : <CancelIcon color="error" sx={{ fontSize: 18 }} />}
                    <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.actorName || a.actorId}
                    </Typography>
                    <Tooltip title={a.dominantNeed || ''}>
                      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.dominantNeed || '—'}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color={a.converted ? 'text.secondary' : 'error'}>
                      {a.converted ? `min ${a.minBudget?.toFixed?.(2) ?? a.minBudget}` : `drop #${a.dropIndex}`}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Violations */}
            {card.violations.length > 0 && (
              <Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <WarningAmberIcon color="warning" sx={{ fontSize: 18 }} />
                  <Typography variant="subtitle2">Violations ({card.violations.length})</Typography>
                </Stack>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {card.violations.map((v, i) => {
                    const ref = card.screenRefs?.[v.screenIndex];
                    const navigable = Boolean(ref && onJumpToScreen);
                    return (
                      <Stack
                        key={`${v.type}-${v.screenIndex}-${i}`}
                        direction="row"
                        spacing={0.5}
                        alignItems="flex-start"
                        data-testid={`motivation-scorecard-violation-${i}`}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          • #{v.screenIndex} “{v.label}” — {v.message}
                        </Typography>
                        {navigable && (
                          <Tooltip title="Focus this screen on the canvas">
                            <Button
                              size="small"
                              onClick={() => onJumpToScreen!(ref!, v.screenIndex)}
                              data-testid={`motivation-scorecard-violation-go-${i}`}
                              sx={{ minWidth: 'auto', px: 0.75, py: 0, textTransform: 'none', flexShrink: 0 }}
                            >
                              go →
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Drop hotspots */}
            {card.dropHotspots.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Drop hotspots</Typography>
                <Stack spacing={0.5}>
                  {card.dropHotspots.map((h) => (
                    <Typography key={h.screenIndex} variant="caption" color="text.secondary">
                      • #{h.screenIndex} “{h.label}” — weight {h.weight} ({h.actorIds.length} actors)
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Under-served needs */}
            {card.underservedNeeds.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Under-served needs</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {card.underservedNeeds.map((u) => (
                    <Chip key={u.needId} size="small" variant="outlined" label={`${u.group} (${u.demandWeight.toFixed(1)})`} />
                  ))}
                </Stack>
              </Box>
            )}

            {card.computedAt && (
              <Typography variant="caption" color="text.disabled">
                computed {new Date(card.computedAt).toLocaleString()} · {card.storedProfiles ?? 0} stored / {card.derivedProfiles ?? 0} derived profiles
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};
